import React, { useState, useEffect } from 'react';
import axios from '../utils/api';
import './Preview.css';

type DeviceRestrictions = Record<string, boolean>;

interface ContentComponent {
  id?: number;
  component_name: string;
  variant_code: string;
  file_path: string;
  css_file_path?: string;
  position_min: number;
  position_max?: number;
  max_usage: number;
  required_components: string[];
  incompatible_components: string[];
  template_restrictions: string[];
  device_restrictions?: DeviceRestrictions;
  performance_impact: string;
  is_active: boolean;
}

interface HtmlTemplate {
  id?: number;
  name: string;
  layout_type: string;
  description?: string;
  html_content?: string;
  css_content?: string;
  is_responsive: boolean;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

interface PreviewProps {
  component?: ContentComponent | null;
  template?: HtmlTemplate | null;
  onClose: () => void;
}

const Preview: React.FC<PreviewProps> = ({ component, template, onClose }) => {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [cssContent, setCssContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [previewMode, setPreviewMode] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  useEffect(() => {
    if (component) {
      loadComponentPreview();
    } else if (template) {
      loadTemplatePreview();
    }
    // Нам важно не менять поведение (обновление предпросмотра при смене component/template),
    // поэтому оставляем зависимости как раньше.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [component, template]);

  const loadComponentPreview = async () => {
    if (!component?.id) return;

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      // Загружаем HTML код компонента
      let html = '';
      let css = '';

      if (component.file_path) {
        try {
          const htmlResponse = await axios.get(`/api/admin/components/${component.id}/html`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          html = htmlResponse.data.content || getDefaultComponentHtml(component);
        } catch {
          html = getDefaultComponentHtml(component);
        }
      }

      if (component.css_file_path) {
        try {
          const cssResponse = await axios.get(`/api/admin/components/${component.id}/css`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          css = cssResponse.data.content || getDefaultComponentCss(component);
        } catch {
          css = getDefaultComponentCss(component);
        }
      }

      setHtmlContent(html);
      setCssContent(css);
    } catch (error: unknown) {
      setError(`Ошибка загрузки компонента: ${getApiErrorMessage(error, 'Неизвестная ошибка')}`);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplatePreview = async () => {
    if (!template) return;

    setLoading(true);
    setError('');

    try {
      // Используем содержимое шаблона или создаем демо-версию
      const html = template.html_content || getDefaultTemplateHtml(template);
      const css = template.css_content || getDefaultTemplateCss(template);

      setHtmlContent(html);
      setCssContent(css);
    } catch (error: unknown) {
      setError(`Ошибка загрузки шаблона: ${getApiErrorMessage(error, 'Неизвестная ошибка')}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="preview-overlay">
      <div className="preview-modal">
        <div className="preview-header">
          <h2>{getPreviewTitle()}</h2>
          <div className="preview-controls">
            <div className="device-selector">
              <button
                className={previewMode === 'desktop' ? 'active' : ''}
                onClick={() => setPreviewMode('desktop')}
                title="Десктоп"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 5h16v10H4z" />
                  <path d="M8 19h8" />
                  <path d="M12 15v4" />
                </svg>
              </button>
              <button
                className={previewMode === 'tablet' ? 'active' : ''}
                onClick={() => setPreviewMode('tablet')}
                title="Планшет"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="7" y="2" width="10" height="20" rx="2" ry="2" />
                  <path d="M11 18h2" />
                </svg>
              </button>
              <button
                className={previewMode === 'mobile' ? 'active' : ''}
                onClick={() => setPreviewMode('mobile')}
                title="Мобильный"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="8" y="2" width="8" height="20" rx="2" ry="2" />
                  <path d="M11 18h2" />
                </svg>
              </button>
            </div>
            <button className="close-btn" onClick={onClose}>
              ✕
            </button>
          </div>
        </div>

        <div className="preview-content">
          {loading && (
            <div className="preview-loading">
              <div className="spinner"></div>
              <p>Загрузка предпросмотра...</p>
            </div>
          )}

          {error && (
            <div className="preview-error">
              <p>{error}</p>
              <button onClick={() => component ? loadComponentPreview() : loadTemplatePreview()}>
                Попробовать снова
              </button>
            </div>
          )}

          {!loading && !error && htmlContent && (
            <div className="preview-frame-container">
              <iframe
                className="preview-frame"
                sandbox="allow-scripts"
                style={{ width: getPreviewWidth(previewMode) }}
                srcDoc={`
                  <style>${cssContent}</style>
                  ${htmlContent}
                `}
                title={getPreviewTitle(component, template)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Preview;

function getDefaultComponentHtml(comp: ContentComponent): string {
  return `
      <div class="component-preview ${comp.component_name.toLowerCase().replace(/\s+/g, '-')}">
        <h3>${comp.component_name}</h3>
        <p>Вариант: ${comp.variant_code}</p>
        <div class="component-content">
          <p>Предпросмотр компонента. Контент будет загружен из файла:</p>
          <code>${comp.file_path}</code>
        </div>
        <div class="component-info">
          <span class="badge">Позиция: ${comp.position_min}-${comp.position_max || '∞'}</span>
          <span class="badge">Макс. использований: ${comp.max_usage}</span>
          <span class="badge performance-${comp.performance_impact.toLowerCase()}">${comp.performance_impact}</span>
        </div>
      </div>
    `;
}

function getDefaultComponentCss(comp: ContentComponent): string {
  return `
      .component-preview {
        border: 2px solid #e1e5e9;
        border-radius: 8px;
        padding: 20px;
        margin: 10px;
        background: #fff;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }

      .component-preview h3 {
        margin: 0 0 10px 0;
        color: #2c3e50;
        font-size: 1.2em;
      }

      .component-content {
        margin: 15px 0;
        padding: 15px;
        background: #f8f9fa;
        border-radius: 4px;
        border-left: 4px solid #007bff;
      }

      .component-info {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        margin-top: 15px;
      }

      .badge {
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 0.8em;
        background: #6c757d;
        color: white;
      }

      .performance-low { background: #28a745; }
      .performance-medium { background: #ffc107; color: #212529; }
      .performance-high { background: #dc3545; }

      @media (max-width: 768px) {
        .component-preview {
          padding: 15px;
          margin: 5px;
        }
      }
    `;
}

function getDefaultTemplateHtml(tmpl: HtmlTemplate): string {
  return `
      <!DOCTYPE html>
      <html lang="ru">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${tmpl.name}</title>
      </head>
      <body>
        <div class="template-preview">
          <header class="template-header">
            <h1>${tmpl.name}</h1>
            <p>Тип макета: ${tmpl.layout_type}</p>
            ${tmpl.description ? `<p class="description">${tmpl.description}</p>` : ''}
          </header>
          
          <main class="template-content">
            <section class="demo-section">
              <h2>Предпросмотр шаблона</h2>
              <p>Это предварительный просмотр HTML-шаблона. Контент будет подставлен при генерации.</p>
              
              <div class="features">
                <div class="feature-item">
                  <h3>Адаптивность</h3>
                  <p>${tmpl.is_responsive ? 'Адаптивный дизайн' : 'Фиксированная ширина'}</p>
                </div>
                
                <div class="feature-item">
                  <h3>Статус</h3>
                  <p>${tmpl.is_active ? 'Активен' : 'Неактивен'}</p>
                </div>
              </div>
            </section>
          </main>
          
          <footer class="template-footer">
            <p>Создан: ${tmpl.created_at ? new Date(tmpl.created_at).toLocaleDateString('ru-RU') : 'Неизвестно'}</p>
          </footer>
        </div>
      </body>
      </html>
    `;
}

function getDefaultTemplateCss(tmpl: HtmlTemplate): string {
  return `
      .template-preview {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        line-height: 1.6;
        color: #333;
        max-width: 1200px;
        margin: 0 auto;
        padding: 20px;
      }

      .template-header {
        text-align: center;
        padding: 40px 0;
        border-bottom: 2px solid #e1e5e9;
        margin-bottom: 30px;
      }

      .template-header h1 {
        font-size: 2.5em;
        margin: 0 0 10px 0;
        color: #2c3e50;
      }

      .description {
        font-size: 1.1em;
        color: #666;
        max-width: 600px;
        margin: 20px auto;
      }

      .template-content {
        padding: 20px 0;
      }

      .demo-section {
        background: #f8f9fa;
        padding: 30px;
        border-radius: 8px;
        margin: 20px 0;
      }

      .features {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 20px;
        margin-top: 30px;
      }

      .feature-item {
        background: white;
        padding: 20px;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }

      .feature-item h3 {
        margin: 0 0 10px 0;
        color: #007bff;
      }

      .template-footer {
        text-align: center;
        padding: 20px 0;
        border-top: 1px solid #e1e5e9;
        margin-top: 40px;
        color: #666;
      }

      @media (max-width: 768px) {
        .template-preview {
          padding: 10px;
        }
        
        .template-header {
          padding: 20px 0;
        }
        
        .template-header h1 {
          font-size: 2em;
        }
        
        .demo-section {
          padding: 20px;
        }
        
        .features {
          grid-template-columns: 1fr;
        }
      }
    `;
}

function getPreviewWidth(previewMode: 'desktop' | 'tablet' | 'mobile'): string {
  switch (previewMode) {
    case 'mobile':
      return '375px';
    case 'tablet':
      return '768px';
    case 'desktop':
      return '100%';
    default:
      return '100%';
  }
}

function getPreviewTitle(component?: ContentComponent | null, template?: HtmlTemplate | null): string {
  if (component) {
    return `Предпросмотр компонента: ${component.component_name}`;
  }
  if (template) {
    return `Предпросмотр шаблона: ${template.name}`;
  }
  return 'Предпросмотр';
}

function getApiErrorMessage(error: unknown, fallbackMessage: string): string {
  if (!error || typeof error !== 'object') {
    return fallbackMessage;
  }

  const errObj = error as Record<string, unknown>;

  const response = errObj.response;
  if (response && typeof response === 'object') {
    const responseObj = response as Record<string, unknown>;
    const data = responseObj.data;
    if (data && typeof data === 'object') {
      const dataObj = data as Record<string, unknown>;
      const apiError = dataObj.error;
      if (typeof apiError === 'string' && apiError.trim()) {
        return apiError;
      }
      const apiMessage = dataObj.message;
      if (typeof apiMessage === 'string' && apiMessage.trim()) {
        return apiMessage;
      }
    }
  }

  const message = errObj.message;
  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  return fallbackMessage;
}
